/**
 * InputSystem - Handles keyboard input and tick timing for the game
 *
 * Responsible for:
 * - Reading Phaser keyboard input and triggering ticks with player direction
 * - Running a timer at configurable intervals for autonomous entity movement
 * - Calling coordinator.processTick() for both input-driven and timer-driven updates
 *
 * This decouples input handling from game logic - the coordinator handles
 * finding the player and queueing moves.
 */

import Phaser from "phaser";
import { Direction } from "../utils/Direction";
import { AutomataCoordinator } from "./AutomataCoordinator";
import { logger } from "../utils/logger";

export class InputSystem {
  private cursors: Phaser.Types.Input.Keyboard.CursorKeys | null = null;
  private keyboard: Phaser.Input.Keyboard.KeyboardPlugin | null = null;
  private repeatDelay: number;
  private coordinator: AutomataCoordinator;

  // Timer for autonomous entity movement
  private tickInterval: number;
  private timer: Phaser.Time.TimerEvent | null = null;
  private scene: Phaser.Scene;

  constructor(
    scene: Phaser.Scene,
    coordinator: AutomataCoordinator,
    repeatDelay: number = 200,
    tickInterval: number = 100 // 100ms = 10 ticks/second for autonomous entities
  ) {
    this.scene = scene;
    this.coordinator = coordinator;
    this.repeatDelay = repeatDelay;
    this.tickInterval = tickInterval;

    // Set up keyboard input
    if (scene.input.keyboard) {
      this.keyboard = scene.input.keyboard;
      this.cursors = this.keyboard.createCursorKeys();
    }

    // Start tick timer for autonomous entity movement
    this.startTimer();
  }

  /**
   * Start the tick timer for autonomous entity movement
   */
  private startTimer(): void {
    this.timer = this.scene.time.addEvent({
      delay: this.tickInterval,
      callback: () => this.onTick(),
      loop: true,
    });

    // Start paused - will be resumed when level starts
    this.pause();
  }

  /**
   * Called on each timer tick for autonomous entity movement
   */
  private onTick(): void {
    const currentTime = Date.now();
    // Call processTick without direction for autonomous entity movement
    this.coordinator.processTick(currentTime);
  }

  /**
   * Update input - call this every frame in GameScene.update()
   * Checks for keyboard input and triggers processTick with player direction
   * Returns true if input was processed
   */
  update(currentTime: number): boolean {
    if (!this.cursors || !this.keyboard) return false;

    let direction: Direction | null = null;

    // Check each arrow key using checkDown for debouncing
    const keys = [
      { key: this.cursors.up, direction: new Direction("up") },
      { key: this.cursors.down, direction: new Direction("down") },
      { key: this.cursors.left, direction: new Direction("left") },
      { key: this.cursors.right, direction: new Direction("right") },
    ];

    for (const { key, direction: dir } of keys) {
      if (!key) continue;

      // Use keyboard.checkDown for debouncing
      const checkResult = this.keyboard.checkDown(key, this.repeatDelay);
      if (checkResult) {
        logger.debug(`[InputSystem] Key pressed: ${dir.value}`);
        direction = dir;
        break;
      }
    }

    // If direction detected, trigger processTick with player direction
    if (direction) {
      this.coordinator.processTick(currentTime, direction);
      return true;
    }

    return false;
  }

  /**
   * Update tick interval (e.g., for slow-motion or speedup)
   */
  setTickInterval(ms: number): void {
    this.tickInterval = ms;

    // Destroy old timer and create new one with new interval
    if (this.timer) {
      const wasPaused = this.timer.paused;
      this.timer.destroy();
      this.startTimer();
      if (wasPaused) {
        this.pause();
      }
    }
  }

  /**
   * Get current tick interval
   */
  getTickInterval(): number {
    return this.tickInterval;
  }

  /**
   * Pause the tick timer
   */
  pause(): void {
    if (this.timer) {
      this.timer.paused = true;
    }
  }

  /**
   * Resume the tick timer
   */
  resume(): void {
    if (this.timer) {
      this.timer.paused = false;
    }
  }

  /**
   * Check if timer is paused
   */
  isPaused(): boolean {
    return this.timer?.paused ?? true;
  }

  /**
   * Cleanup
   */
  destroy(): void {
    if (this.timer) {
      this.timer.destroy();
      this.timer = null;
    }
  }
}
